// ─────────────────────────────────────────────────────────────────────────────
// Las HERRAMIENTAS PROPIAS del Estudio de Video
// ─────────────────────────────────────────────────────────────────────────────
// Eran las 43 tools del "Director de estudio", un segundo agente que vivía adentro del
// editor con su propio drawer y su propio prompt de 42 KB. Desde
// ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-agente el agente del chat es el
// director: el editor registra este catálogo en el puente de pantalla
// (`useRegistrarPantalla`) mientras está montado, y el chat se lo ofrece al modelo SÓLO
// en esa pantalla. Fuera del editor no viaja ninguna, y el método (marca → plan →
// material real → voz → escenas → texto y volumen) vive en la skill `estudio-de-video`,
// que se carga a demanda.
//
// Cada tool tiene que tener su `case` en `executeAgentTool` (VideoEditor.tsx), y
// viceversa; `herramientasDelEstudio.test.ts` lo verifica junto con que la skill las
// nombre a todas. La `etiqueta` es lo que el chat muestra mientras la tool corre (no hay
// tarjeta).
//
// Convención:
//  - `prefill_*` ABREN un panel del editor y lo dejan cargado; NO generan. El usuario
//    revisa y toca "Generar".
//  - `get_*` / `list_*` son de sólo lectura.
//  - El resto cambia la timeline o el proyecto en el acto (reversible con Deshacer),
//    salvo las que cuestan créditos (`erase_object`, `translate_clip_to_english`), que
//    piden confirmación antes.
//
// Se fueron respecto del Director: `ask_user` (el chat pregunta en texto y ofrece chips
// con `sugerir_acciones`) y `list_products` (el catálogo ya está en el prompt del agente
// y `ver_productos` lo muestra con fotos).

import type { HerramientaDePantalla } from '../chat-atlas/puenteDePantalla';
import { CREDITOS_ANIMACION } from '../animations/codigo/reglas';

/** La clave con la que el editor se registra en el puente. */
export const CLAVE_PANTALLA_ESTUDIO = 'editor-video';

/** La skill que enseña a dirigir un video en el editor. */
export const SKILL_DEL_ESTUDIO = 'estudio-de-video';

export const HERRAMIENTAS_DEL_ESTUDIO: HerramientaDePantalla[] = [
  // ── Pedirle cosas al usuario desde el editor ───────────────────────────
  {
    name: 'request_user_upload',
    etiqueta: 'Pidiendo archivos al usuario…',
    description:
      'Abre un panel dedicado en la sidebar izquierda del editor pidiéndole al usuario que suba uno o más archivos (típicamente fotos de producto, del negocio, del local, de una persona/modelo, packaging, referencias de marca, etc.) y espera a que confirme. Usala CADA VEZ que necesites material real del usuario antes de prefillear una escena o imagen IA — una foto real siempre va a rendir mucho mejor que una descripción textual. Esta tool BLOQUEA hasta que el usuario confirma. CUANDO EL USUARIO CONFIRMA, la tool devuelve directamente: (1) un bloque de texto con los assetIds y nombres, y (2) cada IMAGEN subida inline para que vos la VEAS al instante (no necesitás llamar a ninguna otra tool para observar imágenes). Si el usuario sube videos, vienen listados por nombre — para verlos, llamá a analyze_video_quick con el assetId. Si el usuario cancela o cierra el panel sin subir nada, devuelve { cancelled: true }. Podés personalizar el mensaje explicativo y el texto del botón de confirmación.',
    input_schema: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description:
            'Texto explicativo que se muestra arriba del área de subida. Sé específico sobre QUÉ archivos necesitás y POR QUÉ (ej: "Necesito una foto del bolso modelo X en buena luz, preferiblemente sobre fondo neutro, para usarla como referencia de producto en la escena 1.").',
        },
        buttonLabel: {
          type: 'string',
          description:
            'Texto del botón de confirmación al pie del panel (ej: "Usar estas imágenes", "Continuar", "Listo"). Mantenelo corto (1-4 palabras).',
        },
        accept: {
          type: 'string',
          enum: ['image', 'video', 'image+video'],
          description:
            'Qué tipos de archivo aceptar. Default: "image". Usá "video" solo si pedís específicamente un video de referencia.',
        },
        procesar_como_personaje: {
          type: 'boolean',
          description:
            'Poné true SOLO cuando pedís la foto de una PERSONA que va a APARECER en el video (no para productos, packaging ni referencias de estilo). Con true, la foto se procesa a fondo blanco (el mismo flujo del botón del panel que suma una persona guardada, rotulado "Usar modelo" o "Usar avatar" según el rubro de la marca) antes de devolverte el asset — así la persona queda prolija y consistente, no la foto cruda. Tarda ~30-60s. Para productos o referencias, dejalo en false. El nombre del parámetro es interno y no se toca: no se lo repitas al usuario.',
        },
      },
      required: ['message', 'buttonLabel'],
    },
  },

  // ── Sólo lectura: el proyecto ──────────────────────────────────────────
  {
    name: 'get_project_context',
    etiqueta: 'Leyendo el proyecto…',
    description:
      'Devuelve información general del proyecto de video actual: nombre, duración total en segundos, aspect ratio, y recuento de clips por tipo. Llamala al inicio de una conversación para entender el estado del proyecto antes de proponer acciones.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'list_timeline_clips',
    etiqueta: 'Leyendo la timeline…',
    description:
      'Lista todos los clips en la línea de tiempo con sus propiedades (id, tipo, nombre, startTime, duration, trackIndex, prompt de IA y estado de generación si aplica). Úsala cuando necesites razonar sobre el orden, duraciones o contenido ya presente en la timeline.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'list_project_assets',
    etiqueta: 'Buscando archivos del proyecto…',
    description:
      'Lista los assets (videos, audios, imágenes) subidos o generados que están disponibles en el proyecto. Úsala para descubrir material existente que se pueda reutilizar como referencia, primer frame, último frame o para insertar directamente en la timeline. Los ids de assets de turnos anteriores NO los recordás: si necesitás uno que ya se subió antes, buscalo acá (los más recientes primero).',
    input_schema: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: ['video', 'audio', 'image'],
          description: 'Filtrá por tipo de asset. Omitir para listar todos.',
        },
      },
    },
  },
  {
    name: 'analyze_video_quick',
    etiqueta: 'Mirando el video…',
    description:
      'Análisis VISUAL RÁPIDO de un asset de tipo VIDEO del PROYECTO (por assetId; un video ADJUNTO en el chat se mira con ver_video / extraer_frames). Extrae varios frames distribuidos uniformemente del video y te los devuelve como imágenes para que vos (Claude) los observes directamente. Es rápido pero NO incluye audio ni movimiento entre frames. SOLO sirve para videos — NO la uses sobre assets de tipo imagen (las imágenes ya las recibís inline cuando se suben con request_user_upload, o usá list_generated_images para imágenes generadas con IA). Úsala cuando el usuario pregunte por el contenido visual de un video y unos frames clave alcanzan para responder. Si necesitás entender audio/diálogo/música o secuencia temporal precisa, usá analyze_media_deep.',
    input_schema: {
      type: 'object',
      properties: {
        assetId: { type: 'string', description: 'ID del asset (debe ser de tipo video).' },
        frameCount: {
          type: 'integer',
          minimum: 3,
          maximum: 12,
          description: 'Cantidad de frames a extraer. Default: 6.',
        },
      },
      required: ['assetId'],
    },
  },
  {
    name: 'analyze_media_deep',
    etiqueta: 'Analizando el video…',
    description:
      "Análisis PROFUNDO de uno o varios assets de tipo VIDEO y/o AUDIO del PROYECTO usando Gemini (que sí entiende video y audio nativamente). Mandale los assets junto con una pregunta puntual y Gemini devuelve la respuesta en texto. SOLO sirve para video y audio — NO la uses sobre imágenes. Úsala cuando necesites entender narrativa, diálogo, música, efectos sonoros, secuencia temporal precisa o detalles que no se ven en frames sueltos. Es más lento que analyze_video_quick — preferí quick para preguntas visuales rápidas sobre video. Para un video adjunto en el chat (no del proyecto) usá ver_video.",
    input_schema: {
      type: 'object',
      properties: {
        assetIds: {
          type: 'array',
          items: { type: 'string' },
          minItems: 1,
          maxItems: 5,
          description: 'IDs de los assets (video y/o audio) a analizar juntos.',
        },
        prompt: {
          type: 'string',
          minLength: 3,
          description: 'Pregunta o instrucción específica para Gemini sobre los archivos. Sé concreto.',
        },
      },
      required: ['assetIds', 'prompt'],
    },
  },
  {
    name: 'generate_transcript',
    etiqueta: 'Transcribiendo el clip…',
    description:
      'Genera (o reutiliza el cacheado) el transcript palabra por palabra de un clip de video o audio de la timeline. Llamala cuando necesites saber con precisión QUÉ se dice y EN QUÉ MOMENTO en un clip — por ejemplo para encontrar una frase puntual, decidir dónde cortar, o sincronizar texto. Internamente transcribe el archivo *fuente completo* (no sólo la parte visible del clip) y guarda el resultado en el clip; llamadas siguientes sobre el mismo clip devuelven el cache sin volver a transcribir. Si querés leer SÓLO lo que cae dentro del recorte actual del clip (mediaOffset + duration), usá después `get_clip_transcript`. Si querés ver todo lo que se dice en la timeline ya recortada, usá `get_timeline_transcript`. Soporta video/audio subido o ai-generated con audio listo. Idempotente: si el clip ya tiene transcript, devuelve el resumen cacheado al instante.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: {
          type: 'string',
          description: 'ID del clip de la timeline (debe ser video, audio, o ai-generated con audio listo). Usá list_timeline_clips para obtenerlo.',
        },
        force: {
          type: 'boolean',
          description: 'Si true, ignora el cache y vuelve a llamar al servicio de transcripción. Default: false. Usalo sólo si sospechás que el cache quedó desactualizado.',
        },
      },
      required: ['clipId'],
    },
  },
  {
    name: 'get_clip_transcript',
    etiqueta: 'Leyendo la transcripción…',
    description:
      'Devuelve el transcript de UN clip específico, FILTRADO a la ventana actualmente visible (mediaOffset → mediaOffset + duration) — o sea, sólo lo que se dice en la parte del clip que efectivamente está en la timeline después de recortes/splits. Los timestamps se devuelven en segundos de timeline (no del archivo fuente), así podés referenciarlos directamente. Requiere que el clip ya tenga transcript: si no, llamá primero a `generate_transcript` o devolvé un error claro al usuario. Devuelve { clipId, name, startTime, endTime, durationVisible, text, words: [{ word, start, end }] }. Si la ventana visible no contiene ninguna palabra (ej. el clip se trimeó a un silencio), `words` viene vacío y `text` es "".',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'ID del clip.' },
      },
      required: ['clipId'],
    },
  },
  {
    name: 'get_timeline_transcript',
    etiqueta: 'Leyendo la transcripción…',
    description:
      'Devuelve la transcripción combinada de TODA la timeline en orden cronológico, considerando los recortes/splits actuales — es decir, sólo las palabras que efectivamente se escuchan al reproducir el proyecto. Ignora clips silenciados (volume === 0) y clips sin transcript guardado. Devuelve un array de segmentos por clip { clipId, clipName, startTime, endTime, text, words } más un campo `fullText` con todo concatenado. Útil para preguntas tipo "qué dice el video", "encontrá donde se menciona X", "armá una bajada con lo que se dice", sin que te confundas con clips eliminados, recortes o duplicados. Si algún clip de video/audio importante no tiene transcript todavía, viene listado en `missingTranscriptClipIds` para que decidas si llamar a `generate_transcript` para esos clips.',
    input_schema: {
      type: 'object',
      properties: {
        includeMuted: {
          type: 'boolean',
          description: 'Si true, incluye también los clips silenciados. Default: false.',
        },
      },
    },
  },
  {
    name: 'list_generated_images',
    etiqueta: 'Mirando las imágenes del proyecto…',
    description:
      'Lista las imágenes generadas con IA guardadas en este proyecto (separadas de los assets subidos) y te las muestra DIRECTAMENTE como imágenes para que vos (Claude) las observes. Úsala cuando el usuario pregunte por imágenes generadas, quiera reutilizarlas como referencia, o necesites entender qué ya se creó. Devuelve el id de cada imagen junto con la imagen visual.',
    input_schema: {
      type: 'object',
      properties: {
        limit: {
          type: 'integer',
          minimum: 1,
          maximum: 20,
          description: 'Cantidad máxima de imágenes a devolver (las más recientes primero). Default: 12.',
        },
      },
    },
  },
  {
    name: 'import_generated_image_as_asset',
    etiqueta: 'Importando al proyecto…',
    description:
      'Convierte una imagen generada con IA en un asset real del proyecto, listo para usarse como firstFrameAssetId, lastFrameAssetId o refAssetIds en prefill_ai_scene/prefill_ai_image, o para insertarse en la timeline. Devuelve el assetId nuevo. Acepta el id de las que devuelve list_generated_images (campo imagen_id) Y TAMBIÉN un image_id del CHAT: una imagen de generate_image, un frame de extraer_frames o un video de generate_video (source "generada"). Es EL camino para meter en el proyecto lo que generaste desde el chat: nada de lo que sale por generate_* entra solo. Llamala SIEMPRE antes de pasar una imagen generada como referencia/frame, porque las imágenes generadas usan ids de imagenes_generadas y no de assets.',
    input_schema: {
      type: 'object',
      properties: {
        generatedImageId: {
          type: 'string',
          description: 'ID devuelto por list_generated_images (campo imagen_id), o un image_id del chat.',
        },
      },
      required: ['generatedImageId'],
    },
  },

  // ── Catálogo de productos de la marca ──────────────────────────────────
  {
    name: 'use_product',
    etiqueta: 'Importando el producto…',
    description:
      'Importa un producto del catálogo de la marca (los ids de PRODUCTOS del prompt; ver_productos los muestra con foto) como asset de imagen del proyecto y te lo devuelve INLINE para que lo VEAS y confirmes que es el correcto, junto con su assetId. Ese assetId queda listo para usarse como firstFrameAssetId / lastFrameAssetId / refAssetIds en prefill_ai_scene/prefill_ai_image, o para insertarse en la timeline. Es idempotente: importar el mismo producto dos veces reusa el mismo asset. Llamala SIEMPRE antes de pasar un producto como referencia/frame, porque los productos usan ids de catálogo y no de assets del proyecto.',
    input_schema: {
      type: 'object',
      properties: {
        productId: {
          type: 'string',
          description: 'ID del producto del catálogo (los del prompt).',
        },
      },
      required: ['productId'],
    },
  },
  {
    name: 'request_product_selection',
    etiqueta: 'Pidiendo que elija un producto…',
    description:
      'Abre la grilla de PRODUCTOS de la marca en la sidebar izquierda del editor y le pide al usuario que elija uno (o varios) de su catálogo, y espera a que confirme. Es el FALLBACK de use_product: usala cuando NO pudiste identificar el producto por nombre en el catálogo del prompt (no aparece, hay ambigüedad, o el usuario prefiere elegir a mano). Esta tool BLOQUEA hasta que el usuario confirma. CUANDO EL USUARIO ELIGE, la tool devuelve directamente: (1) un bloque de texto con los assetIds y nombres de los productos elegidos (ya importados como assets, listos para usarse como referencia/frame), y (2) cada producto elegido INLINE como imagen para que vos lo VEAS. Si el usuario cierra el panel sin elegir nada, devuelve { cancelled: true }. Podés personalizar el mensaje y el texto del botón.',
    input_schema: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description:
            'Texto explicativo que se muestra arriba de la grilla. Sé específico sobre QUÉ producto buscás y POR QUÉ (ej: "Elegí el producto que querés mostrar en la escena 1 — no encontré ninguno que se llame \'Verona\' en tu catálogo.").',
        },
        buttonLabel: {
          type: 'string',
          description:
            'Texto del botón de confirmación (ej: "Usar este producto", "Listo"). Solo se muestra en modo multi-selección; en selección simple basta con tocar el producto. Mantenelo corto (1-4 palabras).',
        },
        multiple: {
          type: 'boolean',
          description:
            'Si true, el usuario puede marcar varios productos y confirmar con el botón (útil cuando querés combinar 2-3 productos como referencias — prefill_ai_scene acepta hasta 4 refAssetIds). Default: false (toca un producto y se resuelve al instante).',
        },
      },
      required: ['message', 'buttonLabel'],
    },
  },
  {
    name: 'extract_clip_frame_as_asset',
    etiqueta: 'Sacando el frame del clip…',
    description:
      "Extrae el primer o último frame de un clip de la timeline (video o ai-generated) y lo guarda como un asset de imagen del proyecto. Devuelve el assetId resultante, listo para usarse como firstFrameAssetId/lastFrameAssetId/refAssetIds. Útil para encadenar escenas (p.ej. usar el último frame de un clip como primer frame de la próxima escena IA).",
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'ID del clip de la timeline.' },
        which: {
          type: 'string',
          enum: ['first', 'last'],
          description: "'first' = primer frame; 'last' = último frame.",
        },
      },
      required: ['clipId', 'which'],
    },
  },
  {
    name: 'list_available_voices',
    etiqueta: 'Buscando voces…',
    description:
      'Lista las voces IA reales (TTS ElevenLabs) disponibles, con id, nombre, sexo, idioma, acento y descripción. Se usan tanto para voz en off (prefill_ai_audio type voice) COMO para el Modo Diálogo de prefill_ai_scene (sceneMode personas, donde la persona habla a cámara). Las voces en español son de acento ARGENTINO (rioplatense) y HAY varias disponibles: llamala SIEMPRE que vayas a armar una escena con una persona hablando o una voz en off para elegir una acorde al tono/público — nunca asumas que no hay voces en español.',
    input_schema: {
      type: 'object',
      properties: {
        language: {
          type: 'string',
          description:
            "Filtro opcional por idioma. El match es tolerante: podés mandar 'es' o 'español' (matchea las voces en español/argentino), 'en' o 'inglés', indistintamente.",
        },
        sex: {
          type: 'string',
          enum: ['masculino', 'femenino'],
          description:
            "Filtro opcional por sexo de la voz. El match tolera las variantes 'masculina'/'femenina'.",
        },
      },
    },
  },

  // ── La marca del proyecto (brand_profile) ──────────────────────────────
  {
    name: 'get_brand_context',
    etiqueta: 'Leyendo la marca del proyecto…',
    description:
      'Devuelve el brand_profile asociado al proyecto actual: id, name, context (texto libre con la memoria de la marca que se va acumulando), colors (paleta de hex strings) y fonts (objeto con la fuente asignada a cada rol jerárquico: antetitulo, titulo, subtitulo, cuerpo, detalles — cualquiera puede faltar). Es la marca DEL EDITOR (la que el proyecto usa para textos y placas), distinta de la ficha de marca del prompt. Llamala al principio para adaptar las propuestas al tono e identidad de la marca, y SIEMPRE antes de update_brand_context / update_brand_colors / update_brand_fonts para leer el estado actual y no pisar información.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'update_brand_context',
    etiqueta: 'Guardando en la marca del proyecto…',
    description:
      "Actualiza el campo 'context' (texto libre) del brand_profile del proyecto actual. Usalo para recordar información relevante sobre la marca que surja en la conversación: descripción del negocio, tono, público, productos, preferencias visuales, reglas de comunicación, referencias, etc. El contexto es acumulativo: por defecto (mode='append') se agrega al texto existente en un nuevo párrafo. Usa mode='replace' SOLO si el usuario pide explícitamente reemplazar/resetear la memoria. SIEMPRE llamá a get_brand_context primero para no duplicar información.",
    input_schema: {
      type: 'object',
      properties: {
        context: {
          type: 'string',
          description:
            "Texto con la información a guardar. Si mode='append', este texto se agrega como nuevo párrafo al contexto existente. Si mode='replace', reemplaza todo el contexto actual.",
          minLength: 1,
        },
        mode: {
          type: 'string',
          enum: ['append', 'replace'],
          description: "'append' (default) agrega el texto como un nuevo párrafo. 'replace' sobreescribe todo.",
        },
      },
      required: ['context'],
    },
  },
  {
    name: 'update_brand_colors',
    etiqueta: 'Guardando los colores de la marca…',
    description:
      "Actualiza la paleta de colores del brand_profile del proyecto actual. La paleta se guarda como un array de strings hex (ej: ['#1644eb','#ffffff']). Usá mode='add' para sumar colores nuevos sin pisar los existentes (los duplicados se descartan), mode='remove' para sacar colores específicos, o mode='replace' para reemplazar toda la paleta. SIEMPRE llamá a get_brand_context antes para conocer la paleta actual.", // hex-de-marca-ok: ejemplo de hex en un texto
    input_schema: {
      type: 'object',
      properties: {
        colors: {
          type: 'array',
          description: "Array de colores en formato hex con #, ej: ['#1644eb','#ff7700']. En mode='add' se agregan al final; en 'remove' se quitan los que coincidan; en 'replace' es la paleta completa nueva.", // hex-de-marca-ok: ejemplo de hex en un texto
          items: { type: 'string' },
          minItems: 1,
        },
        mode: {
          type: 'string',
          enum: ['add', 'remove', 'replace'],
          description: "'add' (default) suma sin duplicar. 'remove' quita los colores indicados. 'replace' sobreescribe la paleta entera.",
        },
      },
      required: ['colors'],
    },
  },
  {
    name: 'update_brand_fonts',
    etiqueta: 'Guardando las fuentes de la marca…',
    description:
      "Asigna fuentes a los ROLES jerárquicos de la marca: antetitulo, titulo, subtitulo, cuerpo, detalles. Cada rol tiene UNA fuente (o ninguna). Las familias deben coincidir con las del catálogo del editor (sans/serif/display/handwriting). Usalo cuando el usuario expresa preferencia tipográfica para la marca ('para los títulos usá Anton', 'el cuerpo siempre Inter', 'sacá la fuente de los detalles'). Por defecto (mode='merge') solo modifica los roles que envíes y deja los demás como están; usá mode='replace' SOLO si el usuario pide redefinir todas las fuentes desde cero. Para LIMPIAR un rol específico, mandalo con valor null. SIEMPRE llamá a get_brand_context antes.",
    input_schema: {
      type: 'object',
      properties: {
        assignments: {
          type: 'object',
          description: "Mapa de rol → familia. Roles válidos: 'antetitulo', 'titulo', 'subtitulo', 'cuerpo', 'detalles'. El valor es el nombre de la familia (ej: 'Anton', 'Inter', 'Playfair Display'), o null para borrar la asignación de ese rol. Mandá solo los roles que querés tocar.",
          properties: {
            antetitulo: { type: ['string', 'null'] },
            titulo: { type: ['string', 'null'] },
            subtitulo: { type: ['string', 'null'] },
            cuerpo: { type: ['string', 'null'] },
            detalles: { type: ['string', 'null'] },
          },
          additionalProperties: false,
        },
        mode: {
          type: 'string',
          enum: ['merge', 'replace'],
          description: "'merge' (default) toca solo los roles enviados. 'replace' resetea TODOS los roles y deja únicamente los que mandes en `assignments`.",
        },
      },
      required: ['assignments'],
    },
  },
  {
    name: 'get_brand_assets',
    etiqueta: 'Mirando los logos y elementos de la marca…',
    description:
      "Devuelve las imágenes guardadas en la sección Imágenes de la marca (logos + elementos visuales reutilizables) y te las muestra DIRECTAMENTE como imágenes para que vos (Claude) las observes. Cada item viene con: id, tipo ('logo' o 'element'), name, width, height, y la imagen visual. Usala SIEMPRE antes de prefillear cualquier escena/imagen IA donde tenga sentido incluir el logo o un elemento de marca (placas, badges, marcos), para saber qué tiene la marca disponible. Si la marca no tiene logos/elementos cargados pero el usuario los pide o los necesitarías, llamá a open_brand_panel(tab='imagenes') para que el usuario los suba. Para usar uno de estos assets como referencia, primer/último frame o adjunto: el id devuelto NO es un assetId del proyecto — usalo con import_brand_asset_to_project para convertirlo en un asset del proyecto antes de pasarlo a prefill_*.",
    input_schema: {
      type: 'object',
      properties: {
        tipo: {
          type: 'string',
          enum: ['logo', 'element'],
          description: "Filtrá por tipo. 'logo' = versiones del logo de la marca. 'element' = elementos visuales reutilizables (badges, iconos, marcos, ilustraciones). Omitir para listar ambos.",
        },
      },
    },
  },
  {
    name: 'import_brand_asset_to_project',
    etiqueta: 'Importando la imagen de marca…',
    description:
      'Convierte una imagen de la marca (logo o elemento, devuelta por get_brand_assets) en un asset del proyecto actual, listo para usarse como firstFrameAssetId, lastFrameAssetId o refAssetIds en prefill_ai_scene/prefill_ai_image. Devuelve el assetId nuevo. Llamala SIEMPRE antes de pasar una imagen de marca como referencia, porque las imágenes de marca viven en otra tabla y otro bucket que los assets del proyecto.',
    input_schema: {
      type: 'object',
      properties: {
        brandAssetId: {
          type: 'string',
          description: 'ID devuelto por get_brand_assets (campo id).',
        },
      },
      required: ['brandAssetId'],
    },
  },
  {
    name: 'open_brand_panel',
    etiqueta: 'Abriendo el panel de marca…',
    description:
      "Abre el panel de Marca del editor en una pestaña específica para que el usuario complete información que falta. Usala cuando notes que la marca no tiene cargado algo que necesitarías (logos, elementos visuales, fuentes, colores, memoria) y querés que el usuario lo suba/escriba. Después de llamar a esta tool, RESPONDÉ AL USUARIO en una frase corta indicándole qué tiene que hacer en el panel (ej: 'Te abrí la pestaña Imágenes de tu marca — subí el logo principal así puedo usarlo en los videos'). Pestañas disponibles: 'memoria' (texto libre con el contexto de la marca), 'fuentes' (asignación tipográfica por rol), 'colores' (paleta), 'imagenes' (logos + elementos visuales).",
    input_schema: {
      type: 'object',
      properties: {
        tab: {
          type: 'string',
          enum: ['memoria', 'fuentes', 'colores', 'imagenes'],
          description: 'Pestaña que querés mostrarle al usuario.',
        },
      },
      required: ['tab'],
    },
  },
  {
    name: 'get_ui_context',
    etiqueta: 'Mirando el editor…',
    description:
      'Devuelve el estado actual de la UI del editor: qué panel de la sidebar está abierto (activePanel), qué clip está seleccionado en la timeline (si alguno), el aspect ratio del proyecto, y las opciones/campos actualmente cargados en el panel abierto (prompt, duración, modo, voz, referencias, etc.). Usala antes de proponer cambios o llamar a prefill_* para saber qué hay ya cargado y no pisar trabajo del usuario, o para entender el contexto visual cuando el usuario dice algo como "cambiale la duración" sin especificar.',
    input_schema: { type: 'object', properties: {} },
  },

  // ── Pasar a 1080p un clip borrador (ADR-2026-09-27-borrador-480p-y-pasar-a-1080p) ──
  {
    name: 'finalizar_borrador',
    etiqueta: 'Cambiando el clip a 1080p…',
    description:
      'Pasa a 1080p un clip generado como BORRADOR (calidad profesional en 480p, menos de 7 días, sin ediciones encima): sale la MISMA toma en alta y reemplaza el clip en la timeline. Cuesta 5 créditos por segundo del borrador. Cuesta créditos: pedí confirmación en texto con el costo antes de llamarla. El estado te dice qué clips son borradores vigentes.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'Id del clip borrador en la timeline (list_timeline_clips).' },
      },
      required: ['clipId'],
    },
  },

  // ── Cargar los paneles de IA (no generan: el usuario toca Generar) ─────
  {
    name: 'prefill_ai_scene',
    etiqueta: 'Cargando la escena IA…',
    description:
      "Abre el panel 'Nueva escena con IA' en la UI y pre-carga TODOS los campos con los valores propuestos. NO genera el video automáticamente: el usuario revisa los parámetros y aprieta 'Generar'. Usala cuando el usuario pide crear una escena de video con IA: una escena es UN `prompt` y UNA `duration`. Para varias tomas, una llamada por escena (misma referencia en refAssetIds si tienen que verse continuas). El prompt lo redacta después el escritor de video del backend (el mismo que el modo Redes): escribí qué pasa, no un prompt técnico.",
    input_schema: {
      type: 'object',
      properties: {
        prompt: {
          type: 'string',
          description:
            'Descripción visual detallada de la escena (encuadre, acción, iluminación, estilo, mood). Obligatorio salvo en personas/static.',
        },
        sceneMode: {
          type: 'string',
          enum: ['general', 'personas'],
          description:
            "'general' para escenas sin habla. 'personas' para escenas con una persona hablando (requiere voiceId y dialogue). En modo 'personas' leé también `personType`: el comportamiento del prompt cambia entre static y motion.",
        },
        personType: {
          type: 'string',
          enum: ['static', 'motion'],
          description:
            "Sólo aplica si sceneMode='personas'. Default: 'static'. \n'static' (recomendado, default): la persona habla MIRANDO A CÁMARA con poco movimiento (UGC tipo testimonial). En este modo NO hay descripción visual separada — el campo `dialogue` ES la única entrada de texto, contiene únicamente lo que la persona dice. NO pases `prompt` en static (se ignora). Requiere `firstFrameAssetId` (foto de la persona).\n'motion': la persona habla MIENTRAS hace algo / camina / interactúa con el producto (más cinematográfico). Acá SÍ pasás los dos: `prompt` (qué pasa visualmente) Y `dialogue` (qué dice). Más caro y lento; usalo sólo cuando el movimiento aporta.",
        },
        duration: {
          type: 'number',
          description:
            'Duración en segundos. Entre 4 y 15 por escena en calidad estandar; hasta 30 en calidad profesional.',
          minimum: 4,
          maximum: 30,
        },
        aspectRatio: {
          type: 'string',
          enum: ['16:9', '9:16', '1:1', '4:5'],
          description: 'Proporción de la escena. Si no se especifica, usa el del proyecto.',
        },
        quality: {
          type: 'string',
          enum: ['estandar', 'profesional'],
          description:
            "Calidad de render. Default: 'estandar' (hasta 15s, 1/2/4 créditos/seg según resolución). 'profesional' usa el mejor modelo, permite hasta 30s y cuesta más (2/3/5). No aplica al modo personas.",
        },
        resolution: {
          type: 'string',
          enum: ['480p', '720p', '1080p'],
          description:
            "Resolución final. Default: '720p'. Dialog mode no soporta 480p. En profesional, 480p es BORRADOR: sale barato para probar y después se pasa a 1080p con finalizar_borrador.",
        },
        voiceId: {
          type: 'string',
          description:
            "ID de la voz IA a usar. Sólo tiene sentido si sceneMode='personas'. Elegir con list_available_voices.",
        },
        dialogue: {
          type: 'string',
          description: "Texto literal que dirá la persona en la escena. Sólo si sceneMode='personas'.",
        },
        speechSpeed: {
          type: 'number',
          description: "Velocidad de habla (0.7 lenta … 1.2 rápida). Default: 1.1. Sólo si sceneMode='personas'.",
          minimum: 0.7,
          maximum: 1.2,
        },
        firstFrameAssetId: {
          type: 'string',
          description: 'ID de un asset tipo imagen del proyecto a usar como primer frame.',
        },
        lastFrameAssetId: {
          type: 'string',
          description: 'ID de un asset tipo imagen del proyecto a usar como último frame.',
        },
        refAssetIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'IDs de imágenes del proyecto como referencias visuales/estilo.',
          maxItems: 4,
        },
      },
      required: ['sceneMode'],
    },
  },
  {
    name: 'prefill_ai_audio',
    etiqueta: 'Cargando el audio IA…',
    description:
      "Abre el panel 'Nuevo audio con IA' y pre-carga los campos. NO genera nada: el usuario aprieta 'Generar'. Usala para proponer una voz en off, una pieza musical o un efecto de sonido (puerta, whoosh, aplausos, ambiente).",
    input_schema: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: ['voice', 'music', 'sfx'],
          description:
            "'voice' para voz en off narrada. 'music' para generar música. 'sfx' para un efecto de sonido (1 crédito).",
        },
        prompt: {
          type: 'string',
          description:
            "Si type='voice': el texto literal que dirá la voz. Si type='music': descripción del estilo/género/mood/instrumentos. Si type='sfx': qué suena, concreto (\"puerta de madera que se cierra fuerte\").",
        },
        duration: {
          type: 'number',
          description:
            "Duración en segundos. Si type='music', la música sale EXACTO con ese largo (3 a 300 s) y cuesta 1 crédito cada 30 s: usá el largo del tramo que va a musicalizar. Si type='sfx', de 0,5 a 30 s.",
          // El mínimo es el del efecto: música y voz se acotan a sus propios topes al cargarse.
          minimum: 0.5,
          maximum: 300,
        },
        voiceId: {
          type: 'string',
          description:
            "ID de la voz IA. Obligatorio si type='voice'. Elegir con list_available_voices.",
        },
        speechSpeed: {
          type: 'number',
          description: "Velocidad de habla (0.7..1.2). Default: 1.1. Sólo si type='voice'.",
          minimum: 0.7,
          maximum: 1.2,
        },
        instrumentalOnly: {
          type: 'boolean',
          description: "Sólo si type='music'. True = sin voces ni letras. Default: false.",
        },
      },
      required: ['type', 'prompt', 'duration'],
    },
  },
  {
    name: 'prefill_ai_image',
    etiqueta: 'Cargando la imagen IA…',
    description:
      "Abre el panel de generación de imagen IA del editor y pre-carga el prompt + referencias. NO genera: el usuario aprieta 'Generar'. Usala cuando el usuario pide una imagen estática PARA EL PROYECTO (thumbnail, poster, referencia visual, primer/último frame): lo que sale queda en el proyecto, a diferencia de generate_image del chat.",
    input_schema: {
      type: 'object',
      properties: {
        prompt: { type: 'string', description: 'Descripción visual de la imagen.' },
        aspectRatio: {
          type: 'string',
          enum: ['16:9', '9:16', '1:1', '4:5'],
          description: 'Proporción de la imagen.',
        },
        refAssetIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'IDs de imágenes del proyecto como referencia de estilo/composición.',
          maxItems: 4,
        },
      },
      required: ['prompt'],
    },
  },
  {
    name: 'prefill_edit_generated_image',
    etiqueta: 'Cargando la edición de la imagen…',
    description:
      "Abre una imagen previamente generada en MODO EDICIÓN del panel de imagen IA y pre-llena el prompt de cambio. NO ejecuta el cambio: el usuario revisa y aprieta 'Editar'. Usala cuando el usuario quiere modificar una imagen que ya generó (ej: 'hacela más oscura', 'cambiale el fondo'). Pasale el id de la imagen (de list_generated_images) y la descripción del cambio.",
    input_schema: {
      type: 'object',
      properties: {
        generatedImageId: {
          type: 'string',
          description: 'ID de la imagen generada (campo id de list_generated_images).',
        },
        editPrompt: {
          type: 'string',
          description: 'Descripción del cambio a aplicar (ej: "hacela en blanco y negro").',
          minLength: 1,
        },
      },
      required: ['generatedImageId', 'editPrompt'],
    },
  },
  {
    name: 'prefill_edit_scene_frame',
    etiqueta: 'Cargando la edición del frame…',
    description:
      "Abre el primer o último frame actualmente cargado en el panel de escena IA en una pantalla de preview con su prompt de edición pre-llenado. NO ejecuta el cambio: el usuario aprieta 'Editar' y se genera una nueva imagen con el frame original como referencia. Usala cuando el usuario quiere modificar el frame (ej: 'al primer frame ponele un atardecer'). Requiere que el panel ai-scene esté en modo 'frames' y que el slot ya tenga una imagen.",
    input_schema: {
      type: 'object',
      properties: {
        which: {
          type: 'string',
          enum: ['first', 'last'],
          description: "Qué frame editar: 'first' (primer frame) o 'last' (último frame).",
        },
        editPrompt: {
          type: 'string',
          description: 'Descripción del cambio a aplicar al frame.',
          minLength: 1,
        },
      },
      required: ['which', 'editPrompt'],
    },
  },
  {
    name: 'nudge_image_slot',
    etiqueta: 'Señalando dónde subir…',
    description:
      "Hace saltar (3 veces) una zona del panel donde el usuario debe subir o agregar una imagen, para guiar visualmente la atención. Usala SÓLO cuando ya le pediste por chat al usuario que adjunte/suba una imagen y querés resaltar EXACTAMENTE dónde tiene que hacerlo. Requiere que el panel correspondiente esté abierto (prefill_ai_scene o prefill_ai_image antes).",
    input_schema: {
      type: 'object',
      properties: {
        slots: {
          type: 'array',
          items: {
            type: 'string',
            enum: ['scene-firstframe', 'scene-lastframe', 'scene-ref', 'image-attach'],
          },
          minItems: 1,
          maxItems: 4,
          description:
            "Qué zona(s) hacer saltar. 'scene-firstframe'/'scene-lastframe' son los slots de primer/último frame en el panel de escena IA. 'scene-ref' es el botón 'Añadir imagen' de referencia en el panel de escena IA. 'image-attach' es el botón 'Añadir' del panel de generar imagen IA.",
        },
      },
      required: ['slots'],
    },
  },

  // ── La timeline (directo, reversible con Deshacer) ─────────────────────
  {
    name: 'add_text_clip',
    etiqueta: 'Agregando el texto…',
    description:
      'Agrega un clip de texto directamente en la timeline (no requiere confirmación del usuario: es reversible con Deshacer). Podés configurar tipografía, tamaño, color, alineación y posición. Si omitís campos tipográficos, se usan defaults sensatos o el preset indicado en `style`.',
    input_schema: {
      type: 'object',
      properties: {
        text: { type: 'string', description: 'Texto a mostrar.' },
        style: {
          type: 'string',
          enum: ['titulo', 'subtitulo', 'cuerpo', 'animado'],
          description: 'Preset opcional de estilo (define tamaño/peso base si no se pasan explícitos).',
        },
        startTime: {
          type: 'number',
          description:
            'Tiempo en segundos donde insertar. Si se omite, se agrega al final de la pista de texto.',
        },
        duration: {
          type: 'number',
          description: 'Duración en segundos. Default: 3.',
          minimum: 0.5,
          maximum: 60,
        },
        fontFamily: {
          type: 'string',
          description: 'Familia tipográfica. SOLO una del catálogo del editor (ej: "Inter", "Montserrat", "Bebas Neue", "Oswald", "Anton", "Roboto Condensed", "Barlow Condensed", "Playfair Display") o una tipografía que la marca subió. Cualquier otro nombre se rechaza (el error lista las disponibles): el video exportado no la tiene y saldría con otra letra. Default: la del editor.',
        },
        fontSizePct: {
          type: 'number',
          description: 'Tamaño como porcentaje de la altura del canvas (2–30). Default: 8.',
          minimum: 2,
          maximum: 30,
        },
        fontWeight: {
          type: 'string',
          enum: ['normal', 'bold'],
          description: 'Peso tipográfico. Default: "normal".',
        },
        fontStyle: {
          type: 'string',
          enum: ['normal', 'italic'],
          description: 'Estilo tipográfico. Default: "normal".',
        },
        textColor: {
          type: 'string',
          description: 'Color del texto en hexadecimal (ej: "#FFFFFF"). Default: "#FFFFFF".',
        },
        textAlign: {
          type: 'string',
          enum: ['left', 'center', 'right'],
          description: 'Alineación horizontal. Default: "center".',
        },
        textXPct: {
          type: 'number',
          description: 'Posición horizontal del centro del texto, 0–100 % del ancho del canvas. Default: 50.',
          minimum: 0,
          maximum: 100,
        },
        textYPct: {
          type: 'number',
          description: 'Posición vertical del centro del texto, 0–100 % del alto del canvas. Default: 50.',
          minimum: 0,
          maximum: 100,
        },
      },
      required: ['text'],
    },
  },
  // ── Animaciones por código (ADR-2026-09-24-animacion-por-codigo) ───────
  // El agente NO escribe el código: describe la pieza y un generador del backend
  // (Sonnet con las reglas de Remotion) escribe el componente. La tool crea el
  // clip, lo monta en el sandbox del preview y devuelve 3 capturas para que el
  // agente se mire antes de avisar.
  {
    name: 'crear_animacion',
    etiqueta: 'Agregando la animación…',
    description:
      `Crea una animación NUEVA escrita a medida (un componente React de Remotion que escribe un generador del backend a partir de tu brief) y la agrega a la timeline como un clip de animación en una capa nueva de Elementos. Sirve para placas de precio o descuento, títulos con movimiento, sellos, intros con logo, showcase de 2 a 4 fotos de producto, cierres con CTA: cualquier pieza que las plantillas fijas no cubren. tipo="superpuesta" = fondo transparente sobre el video (placas, títulos); tipo="escena" = ocupa toda la pantalla con fondo propio (intro, showcase, cierre). El brief tiene que ser CONCRETO: textos exactos que se ven, colores (o "los de la marca"), ritmo (rápido/suave), de dónde entra y cómo sale, qué asset va dónde. Los assetIds (de list_project_assets, use_product, get_brand_assets importado, o request_user_upload) llegan al código como fotos/logo; máximo 6. La marca (colores y fuentes de get_brand_context) se manda sola. Tarda 15 a 40 segundos. DEVUELVE el clipId, un resumen, la duración Y 3 capturas (inicio, medio, fin) del preview: MIRALAS antes de contestar; si algo está cortado, ilegible o feo, corregilo con modificar_animacion sin preguntar. Si devuelve error de compilación, reformulá el pedido (más simple) y volvé a llamar. CUESTA ${CREDITOS_ANIMACION.crear} créditos por animación creada: confirmá con el usuario en texto, diciendo el costo, antes de llamarla (si el pedido falla no se cobra). Para retocar una que ya existe usá modificar_animacion, NO esta.`,
    input_schema: {
      type: 'object',
      properties: {
        brief: {
          type: 'string',
          description:
            'Qué se ve y cómo se mueve, en detalle: textos exactos, jerarquía, colores, tipografía si el usuario la pidió, entrada/salida, ritmo, qué asset va dónde (por su descripción). Ej: "Placa de descuento: arriba \"HOY\" chico, en el centro \"30% OFF\" enorme en el rojo de la marca con fuente Bebas Neue, abajo \"solo por 24 hs\" en blanco. Entra deslizándose desde abajo con rebote suave en el primer medio segundo, queda quieta y se va desvaneciendo al final. Fondo transparente."',
        },
        tipo: {
          type: 'string',
          enum: ['superpuesta', 'escena'],
          description: 'superpuesta (transparente, encima del video) o escena (pantalla completa con fondo).',
        },
        duracionSeg: {
          type: 'number',
          description: 'Duración del clip en segundos (1 a 60). Si no la mandás, el generador propone una (típicamente 3 a 6 s).',
        },
        assetIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Ids de assets de IMAGEN del proyecto (fotos, logo) que el código va a usar, en el orden en que los nombra el brief. Máximo 6.',
        },
        startTime: {
          type: 'number',
          description: 'Segundo de la timeline donde empieza. Por defecto, donde está el playhead.',
        },
        nombre: {
          type: 'string',
          description: 'Nombre corto del clip en la timeline (ej: "Placa 30% OFF"). Por defecto sale del brief.',
        },
      },
      required: ['brief', 'tipo'],
    },
  },
  {
    name: 'modificar_animacion',
    etiqueta: 'Cambiando la animación…',
    description:
      `Cambia una animación por código que YA existe (creada con crear_animacion): el generador recibe el código actual y tu pedido de cambio, y devuelve la pieza completa modificada, que queda como una VERSIÓN nueva del mismo clip (las anteriores se conservan; volver_a_version_animacion las recupera). Usala para todo retoque —"más lento", "con el logo a la derecha", "en azul", "el texto más grande", "que dure 5 segundos"— y también para corregir lo que viste mal en las capturas. Describí el cambio en concreto y sólo el cambio; lo demás se conserva. Devuelve 3 capturas nuevas: miralas. Si devuelve error, simplificá el pedido. CUESTA ${CREDITOS_ANIMACION.modificar} créditos por cambio: confirmá con el usuario antes (decile el costo), incluso para arreglar algo que viste mal en las capturas; volver_a_version_animacion es gratis.`,
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'Id del clip de animación (de list_timeline_clips o de crear_animacion).' },
        cambios: {
          type: 'string',
          description: 'Qué cambiar, concreto: "más lento (que la entrada dure 1 s)", "agregá el logo (asset 0) arriba a la derecha, chico", "el fondo en el azul de la marca".',
        },
        duracionSeg: {
          type: 'number',
          description: 'Nueva duración del clip en segundos, sólo si el cambio la pide.',
        },
      },
      required: ['clipId', 'cambios'],
    },
  },
  {
    name: 'volver_a_version_animacion',
    etiqueta: 'Cambiando la versión de la animación…',
    description:
      'Vuelve una animación por código a una versión anterior (o posterior) de las que guarda el clip, sin llamar al generador. list_timeline_clips muestra las versiones de cada animación (número 1-based y resumen) y cuál es la actual. Usala cuando el usuario dice "volvé a la anterior", "dejá la primera", "la de antes estaba mejor". Instantánea y gratis.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'Id del clip de animación.' },
        version: { type: 'number', description: 'Número de versión (1 = la primera), como lo muestra list_timeline_clips.' },
      },
      required: ['clipId', 'version'],
    },
  },
  // ── Transiciones, entradas/salidas y subtítulos ─────────────────────────
  // Tres cosas que el editor ya hacía para el usuario y el agente no podía tocar
  // (ADR-2026-09-24-el-agente-transiciona-anima-y-subtitula). Usan los MISMOS
  // setters que los paneles.
  {
    name: 'poner_transicion',
    etiqueta: 'Agregando la transición…',
    description:
      'Pone (o cambia, o quita) la transición ENTRE dos clips de video contiguos de la misma pista. La transición pertenece al clip que ENTRA (el segundo): pasá su clipId. El clip anterior tiene que terminar donde éste empieza (mismo trackIndex, pegados o solapados); si no hay un clip anterior contiguo, devuelve error y hay que moverlos primero con move_clip. Tipos: crossfade (fundido cruzado), fade-to-black (a negro), slide / slide-left / slide-up / slide-down (el nuevo entra deslizándose), zoom, reveal (barrido), diapositivas, stretch-horizontal / stretch-vertical (estirado), y los overlays light-leak (flash de luz cálida), plastic, distorsion, vhs, vintage, glitch. Si no mandás duracionSeg usa la típica del tipo (0,3 s; light-leak 0,5; vhs y vintage 1). tipo="ninguna" quita la transición. Reversible con Deshacer; no cuesta créditos.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'Id del clip que ENTRA (el segundo del par), de list_timeline_clips.' },
        tipo: {
          type: 'string',
          enum: [
            'crossfade', 'fade-to-black', 'slide', 'slide-left', 'slide-up', 'slide-down', 'zoom',
            'reveal', 'diapositivas', 'stretch-horizontal', 'stretch-vertical', 'light-leak',
            'plastic', 'distorsion', 'vhs', 'vintage', 'glitch', 'ninguna',
          ],
        },
        duracionSeg: { type: 'number', description: 'Duración de la transición en segundos (0,1 a 3). Opcional.', minimum: 0.1, maximum: 3 },
      },
      required: ['clipId', 'tipo'],
    },
  },
  {
    name: 'animar_entrada_salida',
    etiqueta: 'Ajustando la entrada y la salida…',
    description:
      'Le pone movimiento de ENTRADA y/o SALIDA a un clip de texto, de animación (Elemento) o de imagen (una capa de imagen: logo, sello, foto suelta) que ya está en la timeline: cómo aparece y cómo se va. Presets para cualquiera de los tres: fade, slide-up, slide-down, slide-left, slide-right, scale (crece), pop (rebote), blur. Sólo para TEXTO: typewriter (letra por letra), char-cascade (cascada por letra), word-cascade (cascada por palabra). "none" saca el movimiento. Mandá sólo lo que querés cambiar (entrada, salida o las dos); las duraciones van de 0,1 a 3 s (default 0,5). Es lo que hace que un add_text_clip "animado" se mueva de verdad: agregá el texto y después llamá esto. No sirve para video ni audio (para eso, poner_transicion). Reversible; gratis.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'Id del clip de texto, animación o imagen.' },
        entrada: {
          type: 'string',
          enum: ['none', 'fade', 'slide-up', 'slide-down', 'slide-left', 'slide-right', 'scale', 'pop', 'blur', 'typewriter', 'char-cascade', 'word-cascade'],
        },
        salida: {
          type: 'string',
          enum: ['none', 'fade', 'slide-up', 'slide-down', 'slide-left', 'slide-right', 'scale', 'pop', 'blur', 'typewriter', 'char-cascade', 'word-cascade'],
        },
        duracionEntradaSeg: { type: 'number', minimum: 0.1, maximum: 3 },
        duracionSalidaSeg: { type: 'number', minimum: 0.1, maximum: 3 },
      },
      required: ['clipId'],
    },
  },
  {
    name: 'quitar_fondo',
    etiqueta: 'Recortando el fondo de la imagen…',
    description:
      'Le quita el FONDO a una capa de imagen que ya está en la timeline (un producto, un logo, una persona): el clip pasa a mostrar la misma imagen recortada, con transparencia, y el original queda en Archivos. Es un modelo de recorte (BiRefNet), no generativo: el producto queda pixel por pixel igual. Sólo para clips de tipo image (no video, no animación). Tarda 3-8 s. GRATIS, no cobra créditos. Usalo cuando el usuario quiera un logo o producto "sin fondo", "recortado" o "flotando" sobre el video.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'Id del clip de imagen.' },
      },
      required: ['clipId'],
    },
  },
  {
    name: 'generar_subtitulos',
    etiqueta: 'Transcribiendo y subtitulando el clip…',
    description:
      'LA forma de subtitular: transcribe el audio de un clip de video o de audio y agrega los subtítulos a la timeline como una tanda de textos sincronizados palabra por palabra, en el estilo de subtítulos del editor (centrados abajo, blancos, en negrita, ancho 90 %), enganchados al clip (si se mueve o se recorta, los subtítulos lo siguen). NUNCA subtitules con add_text_clip línea por línea: sale mal sincronizado y el usuario no lo puede editar como subtítulos. Tarda 15 a 60 s (transcripción) y devuelve cuántas líneas puso. Si el clip ya tiene subtítulos, no los duplica: avisa, salvo reemplazar=true, que borra los anteriores y genera de nuevo. Si el clip no tiene voz, devuelve error "sin habla". Los subtítulos después se editan con el panel de Texto (y su estilo se aplica a toda la tanda). Gratis.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'Id del clip de video, ai-generated (listo) o audio con voz, de list_timeline_clips.' },
        reemplazar: { type: 'boolean', description: 'true para borrar los subtítulos que ese clip ya tenía y generar de nuevo.' },
      },
      required: ['clipId'],
    },
  },
  {
    name: 'add_asset_to_timeline',
    etiqueta: 'Insertando en la timeline…',
    description:
      'Inserta un asset existente del proyecto (video/audio) en la timeline. Usá list_project_assets primero para obtener el assetId (o el que te devolvió import_generated_image_as_asset para un video del chat).',
    input_schema: {
      type: 'object',
      properties: {
        assetId: { type: 'string', description: 'ID del asset a insertar.' },
        startTime: {
          type: 'number',
          description:
            'Tiempo en segundos. Si se omite, va a la pista principal: en el cursor si ahí entra, y si no en el primer hueco después.',
        },
        trackIndex: {
          type: 'integer',
          description: 'Índice de pista (0 = principal). Default: 0.',
          minimum: 0,
        },
      },
      required: ['assetId'],
    },
  },
  {
    name: 'move_clip',
    etiqueta: 'Moviendo el clip…',
    description: 'Cambia la posición (startTime) y opcionalmente la pista (trackIndex) de un clip existente.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'ID del clip a mover.' },
        startTime: { type: 'number', description: 'Nueva posición en segundos.', minimum: 0 },
        trackIndex: {
          type: 'integer',
          description: 'Nueva pista. Omitir para mantener la actual.',
          minimum: 0,
        },
      },
      required: ['clipId', 'startTime'],
    },
  },
  {
    name: 'trim_clip',
    etiqueta: 'Recortando el clip…',
    description:
      'Ajusta la duración de un clip y opcionalmente su mediaOffset (desde qué punto del material original empieza). No pasa del final del material. Una animación gráfica se recorta igual que un video (su material es la animación entera, no se acelera): para alargarla, modificar_animacion con la duración nueva.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string' },
        duration: { type: 'number', description: 'Nueva duración en segundos.', minimum: 0.1 },
        mediaOffset: {
          type: 'number',
          description: 'Segundos a saltar al inicio del material original. Default: sin cambio.',
          minimum: 0,
        },
      },
      required: ['clipId', 'duration'],
    },
  },
  {
    name: 'split_clip',
    etiqueta: 'Dividiendo el clip…',
    description: 'Divide un clip en dos en un tiempo dado (relativo al inicio del clip).',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string' },
        splitAt: {
          type: 'number',
          description: 'Segundos desde el inicio del clip donde dividir (debe ser > 0.1 y < duration - 0.1).',
        },
      },
      required: ['clipId', 'splitAt'],
    },
  },
  {
    name: 'delete_clip',
    etiqueta: 'Borrando el clip…',
    description: 'Elimina un clip de la timeline.',
    input_schema: {
      type: 'object',
      properties: { clipId: { type: 'string' } },
      required: ['clipId'],
    },
  },
  {
    name: 'autocut_interview',
    etiqueta: 'Cortando muletillas y silencios…',
    description:
      "Corta automáticamente las muletillas (eh, este, o sea, mmm, …) y/o los silencios largos de un clip de video con voz, usando su transcripción. El clip se reconstruye más corto (los pedazos que se conservan quedan pegados) y los clips siguientes de SU pista se corren para cerrar el hueco; otras pistas no se tocan. FLUJO OBLIGATORIO: corré primero con dry_run=true para obtener cuántos cortes y cuántos segundos se sacarían, mostráselo al usuario y pedí confirmación; recién entonces volvé a llamar con dry_run=false para aplicar. Requiere que el clip tenga transcript (si no, llamá generate_transcript primero).",
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'Id del clip (de list_timeline_clips).' },
        filler_words: {
          type: 'array',
          items: { type: 'string' },
          description: 'Override de la lista de muletillas (lowercase, sin acentos). Default: lista en español.',
        },
        max_silence_seconds: {
          type: 'number',
          minimum: 0.3,
          maximum: 10,
          description: 'Gap entre palabras mayor a esto = silencio a cortar. Default 2.5 (conservador; bajá a 1.0-1.5 para más agresivo).',
        },
        padding_seconds: {
          type: 'number',
          minimum: 0,
          maximum: 0.6,
          description: 'Margen de seguridad alrededor de cada corte. Default 0.3.',
        },
        remove_filler_words: { type: 'boolean', description: 'Si false, solo corta silencios. Default true.' },
        remove_silences: { type: 'boolean', description: 'Si false, solo corta muletillas. Default true.' },
        dry_run: { type: 'boolean', description: 'Si true, solo calcula y devuelve el resumen sin tocar el timeline. Default false.' },
      },
      required: ['clipId'],
    },
  },
  {
    name: 'erase_object',
    etiqueta: 'Borrando el objeto del video…',
    description:
      'Prepara el borrado de un objeto del video de un clip (magic eraser, Bria): selecciona el clip, pone el cursor ahí y abre "Editar video". NO borra sola: el borrado sólo sabe sacar lo que el usuario marca tocando el objeto, así que después le explicás que toque "Borrar objeto", toque dentro del objeto y confirme. Requiere un clip de VIDEO ya subido a la nube y un tramo de hasta 5 s (más largo devuelve error: pedí dividirlo con S). CUESTA CRÉDITOS (2 por segundo del tramo, máx. 10) y se cobra al terminar; el modal le pide confirmación con el costo. Tarda unos minutos; el clip se reemplaza in-place y el resultado queda también en Archivos.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: {
          type: 'string',
          description: 'Id del clip de video (de list_timeline_clips).',
        },
        prompt: {
          type: 'string',
          description: 'Qué objeto quiere sacar el usuario, para nombrárselo al explicarle dónde tocar. Ej: "la taza".',
        },
      },
      required: ['clipId', 'prompt'],
    },
  },
  {
    name: 'set_clip_volume',
    etiqueta: 'Ajustando el volumen…',
    description:
      'Cambia el volumen de un clip de audio/video. Use 0 para silenciar (aparece badge en la UI).',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string' },
        volume: {
          type: 'number',
          description: 'Volumen: 0 = mute, 1 = normal, 2 = máximo.',
          minimum: 0,
          maximum: 2,
        },
      },
      required: ['clipId', 'volume'],
    },
  },
  {
    name: 'translate_clip_to_english',
    etiqueta: 'Traduciendo el clip al inglés…',
    description:
      'Dobla un clip de video al inglés con lip-sync, manteniendo la voz del speaker original (vía HeyGen Video Translate v3). Solo aplica a clips type="video" o type="ai-generated" en estado "ready". Es asíncrono: el flujo arranca el job en HeyGen y devuelve inmediatamente — el clip queda con aiTranslating=true mientras procesa (puede tardar 5-15 min) y se REEMPLAZA in-place en la timeline cuando termina (mismo startTime/trackIndex, nuevo src y posiblemente nueva duración). Cuesta 0.3 créditos por segundo de duración del clip (mínimo 1 crédito). NO disparar sin confirmación previa del usuario — preguntá primero en texto mostrando el costo estimado.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: {
          type: 'string',
          description:
            'ID del clip de video a traducir. Debe ser type="video" o type="ai-generated" con aiStatus="ready" y tener src cargado.',
        },
      },
      required: ['clipId'],
    },
  },

  {
    name: 'corregir_voz_en_clip',
    etiqueta: 'Corrigiendo la voz del clip…',
    description:
      'Corrige palabras mal dichas en un clip de VIDEO o de AUDIO regenerando sólo las oraciones tocadas con la voz clonada del propio clip (clon efímero + TTS con contexto, empalmado en el mismo hueco). Sirve para pulir una palabra mal pronunciada o cambiar un término sin regenerar el clip entero. Las correcciones van por TEXTO: [{dice:"gamula", deberia_decir:"gamuza"}]; se corrigen TODAS las apariciones y se busca sin distinguir mayúsculas ni puntuación. Cada corrección tiene que tener la MISMA cantidad de palabras de los dos lados (no se puede borrar ni agregar palabras: pedilo palabra por palabra o cambiá otra de la oración). Requiere que el clip tenga transcript (si no, llamá antes a generate_transcript) y que esté subido a la nube. Es GRATIS y no cambia la velocidad (eso se hace aparte). Asíncrono: arranca el trabajo y devuelve al toque; el clip muestra un shimmer mientras procesa (15-60 s) y se REEMPLAZA in-place al terminar (mismo startTime/trackIndex, nuevo src); el original queda en Archivos. Devuelve qué correcciones se aplicaron y cuáles no (con el motivo) para que se lo cuentes al usuario.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: {
          type: 'string',
          description: 'Id del clip de video o audio (de list_timeline_clips).',
        },
        correcciones: {
          type: 'array',
          description: 'Qué dice hoy y qué debería decir, con la misma cantidad de palabras.',
          items: {
            type: 'object',
            properties: {
              dice: { type: 'string', description: 'La palabra (o palabras seguidas) tal como suena hoy.' },
              deberia_decir: { type: 'string', description: 'Por qué reemplazarla, misma cantidad de palabras.' },
            },
            required: ['dice', 'deberia_decir'],
          },
        },
      },
      required: ['clipId', 'correcciones'],
    },
  },
  // ── El proyecto ────────────────────────────────────────────────────────
  {
    name: 'set_project_name',
    etiqueta: 'Renombrando el proyecto…',
    description: 'Renombra el proyecto.',
    input_schema: {
      type: 'object',
      properties: { name: { type: 'string', minLength: 1, maxLength: 120 } },
      required: ['name'],
    },
  },
  {
    name: 'set_project_aspect_ratio',
    etiqueta: 'Cambiando el formato del proyecto…',
    description:
      'Cambia el aspect ratio global del proyecto. Afecta la preview y los defaults de nuevas escenas IA.',
    input_schema: {
      type: 'object',
      properties: {
        aspectRatio: { type: 'string', enum: ['16:9', '9:16', '1:1', '4:5'] },
      },
      required: ['aspectRatio'],
    },
  },
];

export const NOMBRES_HERRAMIENTAS_DEL_ESTUDIO: string[] = HERRAMIENTAS_DEL_ESTUDIO.map((h) => h.name);
